import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { fetchMyTrucks, fetchMyRequests } from "../../utils/matchingApi";
import { transportBodyTypes } from "../../components/search/categories";
import { isCarrierRole, setUnreadCount, countUnread } from "../useUnread";

export const API = process.env.REACT_APP_API_URL;
export const authHeaders = () => ({
  Authorization: `JWT ${window.localStorage.getItem("access")}`,
});

export const BODY_TYPE_OPTIONS = transportBodyTypes
  .filter((bt) => bt.parent_id != null)
  .map((bt) => ({ value: bt.id, label: bt.name }));
const BODY_TYPE_NAMES = new Map(transportBodyTypes.map((bt) => [bt.id, bt.name]));
export const bodyTypeName = (id) =>
  (id && typeof id === "object" ? id.name : BODY_TYPE_NAMES.get(id)) || "—";

// matching service currency ids; the product works in Kyrgyz som (KGS) by default
export const CURRENCIES = [
  { value: 4, label: "KGS (сом)", sign: "сом" },
  { value: 1, label: "USD ($)", sign: "$" },
  { value: 3, label: "KZT (₸)", sign: "₸" },
];
export const currencySign = (id) => CURRENCIES.find((c) => c.value === id)?.sign || (id === 2 ? "₽" : ""); // 2 = legacy RUB, display only

export const RADIUS_OPTIONS = [
  { value: 200, label: "200 км" },
  { value: 50, label: "50 км" },
  { value: 100, label: "100 км" },
  { value: 500, label: "500 км" },
  { value: 2000, label: "Любой" },
];

export const STATUS_META = {
  searching: { cls: "free", label: "Ищет груз" },
  trip: { cls: "trip", label: "В рейсе" },
  tomorrow: { cls: "tomorrow", label: "Освобождается завтра" },
  idle: { cls: "idle", label: "Стоит" },
};

export const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const ymd = (d) => {
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

/** live availability row (or null) -> wireframe status key */
export const deriveState = (lt) => {
  if (!lt) return "idle";
  if (lt.status === "BUSY") return "trip";
  if (lt.status === "ACTIVE") {
    const tomorrow = ymd(Date.now() + 86_400_000);
    if (lt.available_from && ymd(lt.available_from) === tomorrow) return "tomorrow";
    return "searching";
  }
  return "idle";
};

export const fmtMoney = (amount, currency) => {
  if (amount == null) return "по договорённости";
  return `${Number(amount).toLocaleString("ru-RU")} ${currency || ""}`.trim();
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(String(d).length === 10 ? `${d}T00:00:00` : d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
};

export const agoText = (min) => {
  if (min == null) return "";
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  if (min < 1440) return `${Math.round(min / 60)} ч назад`;
  return `${Math.round(min / 1440)} дн назад`;
};

const liveFields = (lt) =>
  lt
    ? {
        state: deriveState(lt),
        expired: lt.status === "EXPIRED",
        status: lt.status,
        country: lt.country || null,
        currency: lt.currency ?? null,
        radius_km: lt.radius_km ?? null,
        available_until: lt.available_until || null,
        city: lt.city || null,
        destinations: lt.destinations || [],
        min_price: lt.min_price ?? null,
        confirmed_minutes_ago: lt.confirmed_minutes_ago,
        available_from: lt.available_from || null,
        comment: lt.comment || null,
        live: true,
      }
    : {
        state: "idle",
        expired: false,
        status: null,
        country: null,
        currency: null,
        radius_km: null,
        available_until: null,
        city: null,
        destinations: [],
        min_price: null,
        confirmed_minutes_ago: null,
        available_from: null,
        comment: null,
        live: false,
      };

const POLL_INTERVAL_MS = 20_000;

/**
 * Vehicles (Django /transports/?owner=me) + live availability + incoming
 * requests (matching service), 20 s polling. Shared by Fleet / Edit pages.
 */
export default function useFleet() {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [liveError, setLiveError] = useState(false);
  const [error, setError] = useState(null);
  // false for CLIENT: matching /truck/* answers 403 for them, so we never call it
  const [canFleet, setCanFleet] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await axios
        .get(`${API}/auth/users/me/`, { headers: authHeaders() })
        .catch(() => null);
      const meId = meRes?.data?.id;
      if (meId && !isCarrierRole(meRes.data.role)) {
        setCanFleet(false);
        setVehicles([]);
        setRequests([]);
        setLiveError(false);
        return;
      }
      setCanFleet(true);
      if (!meId) {
        setError("Не удалось определить пользователя — обновите страницу");
        setVehicles([]);
        setRequests([]);
        return;
      }
      const [transportsRes, live, feed] = await Promise.allSettled([
        axios.get(`${API}/transports/`, { params: { owner: meId }, headers: authHeaders() }),
        fetchMyTrucks(),
        fetchMyRequests(),
      ]).then((r) => r.map((p) => (p.status === "fulfilled" ? p.value : null)));
      setLiveError(!live || !feed);
      const raw = transportsRes?.data;
      const transports = Array.isArray(raw) ? raw : raw?.results || [];
      const mine = transports.filter((t) => t.owner?.id === meId);
      const liveByVehicle = {};
      (live?.trucks || []).forEach((lt) => {
        liveByVehicle[lt.vehicle_id] = lt;
      });
      setVehicles(
        mine.map((t) => ({
          vehicle_id: t.id,
          mark: t.mark || t.model || "—",
          model: t.model || "",
          body_type_id: t.body_type && typeof t.body_type === "object" ? t.body_type.id : t.body_type,
          plate: t.government_number || "—",
          body_type: bodyTypeName(t.body_type),
          max_load_weight: t.max_load_weight,
          max_load_volume: t.max_load_volume,
          photo: t.tractor_photo || null,
          vin: t.vin || "",
          driver_name: t.driver_name || "",
          driver_phone: t.driver_phone || "",
          registration_city: t.registration_city || "",
          vehicle_comment: t.vehicle_comment || "",
          can_transfer: !!t.can_transfer,
          ready_urgent: !!t.ready_urgent,
          passport_doc: t.tech_passport_of_tractor || null,
          insurance_doc: t.insurance_photo || null,
          licence_doc: t.licence_photo || null,
          ...liveFields(liveByVehicle[t.id] || null),
        })),
      );
      setRequests(feed?.matches || []);
    } catch (e) {
      setError("Не удалось загрузить автопарк");
    } finally {
      setLoading(false);
    }
  }, []);

  const canFleetRef = useRef(true);
  canFleetRef.current = canFleet;
  const refreshLive = useCallback(async (force = false) => {
    if (!canFleetRef.current) return;
    if (force !== true && document.visibilityState !== "visible") return;
    try {
      const [live, feed] = await Promise.allSettled([fetchMyTrucks(), fetchMyRequests()]).then(
        (r) => r.map((p) => (p.status === "fulfilled" ? p.value : null)),
      );
      const liveByVehicle = {};
      (live?.trucks || []).forEach((lt) => {
        liveByVehicle[lt.vehicle_id] = lt;
      });
      if (live) {
        setVehicles((prev) =>
          prev.map((v) => ({ ...v, ...liveFields(liveByVehicle[v.vehicle_id] || null) })),
        );
      }
      if (feed) setRequests(feed.matches || []);
    } catch {
      /* keep last state */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pollRef = useRef(null);
  useEffect(() => {
    pollRef.current = setInterval(() => refreshLive(), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [refreshLive]);

  const requestsByVehicle = useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      const list = map.get(r.vehicle_id) || [];
      list.push(r);
      map.set(r.vehicle_id, list);
    }
    return map;
  }, [requests]);

  const newCount = useMemo(() => requests.filter((r) => !r.decided).length, [requests]);

  useEffect(() => {
    if (!loading && canFleet) setUnreadCount(countUnread(requests));
  }, [loading, canFleet, requests]);

  const stats = {
    total: vehicles.length,
    active: vehicles.filter((v) => v.state === "searching").length,
    busy: vehicles.filter((v) => v.state === "trip").length,
    tomorrow: vehicles.filter((v) => v.state === "tomorrow").length,
    newRequests: newCount,
  };

  return { canFleet, loading, vehicles, requests, requestsByVehicle, stats, liveError, error, load, refreshLive };
}
